/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Building2,
  Layers
} from 'lucide-react';
import { subAccountService } from '../services/subAccountService';
import { accountService } from '../services/accountService';
import { serviceRequestTypeService } from '../services/serviceRequestTypeService';
import { useAuth } from '../context/AuthContext';
import toastService from '../utils/toastService';
import BrandingSection from '../components/BrandingSection';
import { useAccountBranding } from '../hooks/useAccountBranding';
import MultiSelect from '../components/MultiSelect';
import { FIELD_LIMITS } from '../utils/constants';
import { validateTextField } from '../utils/validation';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL, SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

const ROUTE_SUB_ACCOUNTS = '/sub-accounts';

const EditSubAccount = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateProfile, refreshBranding } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [form, setForm] = useState({
    name: '',
    slug: '',
    status: 'active',
    externalSubAccountId: '',
    account_name: '',
    theme: '', // empty = use the parent account's theme
    logo_url: '',
    favicon_url: '',
    show_qr_code: true,
    serviceRequestTypeIds: []
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [parentAccounts, setParentAccounts] = useState([]);
  const [availableServiceRequestTypes, setAvailableServiceRequestTypes] = useState([]);
  // Parent account branding — shown as what empty branding fields fall back to
  const accountBranding = useAccountBranding(accountId);

  // Stable upload function — shared by brand logo and workflow logo fields
  const handleLogoUpload = useCallback(async (file) => {
    const res = await subAccountService.uploadLogo(file, id);
    return res.data; // { url: string }
  }, [id]);

  // Stable upload function for favicon
  const handleFaviconUpload = useCallback(async (file) => {
    const res = await subAccountService.uploadLogo(file, id, 'favicon');
    return res.data; // { url: string }
  }, [id]);

  useEffect(() => {
    const fetchAccounts = async () => {
      if (user?.scope === 'account') {
        return;
      }

      try {
        const { data } = await accountService.getAccounts({ limit: 100 });
        setParentAccounts(data.data || data || []);
      } catch (err) {
        toastService.error(`Failed to load ${ACCOUNT_LABEL.pluralLower}`);
      }
    };
    fetchAccounts();
  }, [user]);

  useEffect(() => {
    const loadSubAccount = async () => {
      try {
        setLoading(true);

        // Try to use sub-account data passed from the list view first
        const initialSub = location.state?.sub;

        if (initialSub && (initialSub.id === id || initialSub.uid === id)) {
          const pId = initialSub.accountId || user?.accountUID;
          setAccountId(pId);
          setForm({
            name: initialSub.name || '',
            slug: initialSub.slug || '',
            status: initialSub.status || 'active',
            externalSubAccountId: initialSub.externalSubAccountId || '',
            account_name: initialSub.account?.name || initialSub.accountName || `Unknown ${ACCOUNT_LABEL.singular}`,
            theme: initialSub.theme || '',
            logo_url: initialSub.logoUrl || initialSub.logo_url || '',
            favicon_url: initialSub.faviconUrl || '',
            show_qr_code: initialSub.showQrCode ?? initialSub.show_qr_code ?? true,
            serviceRequestTypeIds: []
          });

          // Fetch full record separately to populate multi-select
          try {
            const { data: full } = await subAccountService.getSubAccountById(pId, id);
            setForm(f => ({
              ...f,
              externalSubAccountId: full.externalSubAccountId || '',
              serviceRequestTypeIds: full.serviceRequestTypeIds || [],
            }));
          } catch (err) {
            // Non-fatal — multi-select just starts empty
          }

          setLoading(false);
          return;
        }

        // If no state, fetch using parentId
        const parentId = user?.accountUID;
        if (!parentId) {
          throw new Error('Missing parent account context');
        }

        const { data } = await subAccountService.getSubAccountById(parentId, id);
        setAccountId(data.accountId || parentId);
        setForm({
          name: data.name,
          slug: data.slug,
          status: data.status,
          externalSubAccountId: data.externalSubAccountId || '',
          account_name: data.account?.name || data.accountName || `Unknown ${ACCOUNT_LABEL.singular}`,
          theme: data.theme || '',
          logo_url: data.logoUrl || data.logo_url || '',
          favicon_url: data.faviconUrl || '',
          show_qr_code: data.showQrCode ?? data.show_qr_code ?? true,
          serviceRequestTypeIds: data.serviceRequestTypeIds || []
        });
      } catch (err) {
        toastService.error(`Failed to load ${SUB_ACCOUNT_LABEL.singularLower}`);
        navigate(ROUTE_SUB_ACCOUNTS);
      } finally {
        setLoading(false);
      }
    };
    loadSubAccount();
  }, [id, navigate, user?.accountUID, location.state]);

  useEffect(() => {
    const loadAvailableTypes = async () => {
      if (!accountId) {
        setAvailableServiceRequestTypes([]);
        return;
      }
      try {
        const { data } = await serviceRequestTypeService.getServiceRequestTypes({ accountUid: accountId, limit: 100 });
        setAvailableServiceRequestTypes(data.data || []);
      } catch (err) {
        setAvailableServiceRequestTypes([]);
      }
    };
    loadAvailableTypes();
  }, [accountId]);

  const validateForm = () => {
    const errors = {};
    const nameError = validateTextField(form.name, `${SUB_ACCOUNT_LABEL.singular} name`, { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT });
    if (nameError) { errors.name = nameError; }
    if (!accountId) { errors.account_id = `Parent ${ACCOUNT_LABEL.singularLower} is required`; }
    setFieldErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleFieldBlur = useCallback((fieldKey, value, fieldLabel, options) => {
    const errorMsg = validateTextField(value, fieldLabel, options);
    setFieldErrors(prev => ({ ...prev, [fieldKey]: errorMsg }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { return; }
    try {
      setSubmitting(true);

      await subAccountService.updateSubAccount(accountId, id, {
        name: form.name.trim(),
        status: form.status,
        externalSubAccountId: form.externalSubAccountId.trim(),
        theme: form.theme,
        logoUrl: form.logo_url,
        faviconUrl: form.favicon_url,
        showQrCode: form.show_qr_code,
        serviceRequestTypeIds: form.serviceRequestTypeIds
      });
      if (user?.subAccountUID && user.subAccountUID === id) {
        updateProfile({
          subAccountLogoUrl: form.logo_url,
          subAccountTheme: form.theme,
          subAccountShowQrCode: form.show_qr_code,
          subAccountFaviconUrl: form.favicon_url
        });
        // Re-resolve logo / favicon / theme (empty fields fall back to the account)
        await refreshBranding().catch(() => {});
      }
      toastService.success(`${SUB_ACCOUNT_LABEL.singular} updated successfully`);
      navigate(ROUTE_SUB_ACCOUNTS);
    } catch (err) {
      const msg = err.response?.data?.message;
      const errorMsg = Array.isArray(msg) ? msg[0] : (msg || `Failed to update ${SUB_ACCOUNT_LABEL.singularLower}`);
      toastService.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${
      fieldErrors[field] ? 'border-error ring-1 ring-error/20' : 'border-base-300'
    }`;

  if (loading) {
    return (
      <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
        <div className="h-4 w-32 bg-base-300/60 rounded animate-pulse"></div>
        <div className="glass-card rounded-2xl p-6 border border-base-content/10">
          <div className="h-7 w-48 bg-base-300/60 rounded mb-2 animate-pulse"></div>
          <div className="h-4 w-80 bg-base-200 rounded animate-pulse"></div>
        </div>
        <div className="glass-card rounded-2xl p-6 border border-base-content/10 space-y-4">
          <div className="h-5 w-40 bg-base-300/60 rounded animate-pulse"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="h-10 bg-base-200/60 rounded-xl animate-pulse"></div>
            <div className="h-10 bg-base-200/60 rounded-xl animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
      {/* Top Breadcrumb */}
      <div>
        <button
          type="button"
          onClick={() => navigate(ROUTE_SUB_ACCOUNTS)}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to {SUB_ACCOUNT_LABEL.plural}
        </button>
      </div>

      {/* Hero Header Banner */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
                Edit Record
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              Update {SUB_ACCOUNT_LABEL.singular}
            </h1>
            <p className="text-xs text-base-content/50">
              Modify {SUB_ACCOUNT_LABEL.singularLower} details, update assigned {SERVICE_REQUEST_LABEL.singularLower} types, and customize branding.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Parent Account - Read-only if platform user */}
        {user?.scope === 'platform' && (
          <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
              <Building2 size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                Organization & Scope
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label htmlFor="parentAccount" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                  Parent {ACCOUNT_LABEL.singular}
                </label>
                <div className="relative">
                  <input
                    id="parentAccount"
                    type="text"
                    readOnly
                    tabIndex={-1}
                    className="w-full px-4 py-2.5 bg-base-200/50 border border-base-300 rounded-xl text-sm text-base-content/60 font-medium outline-none cursor-not-allowed"
                    value={parentAccounts.find(a => a.id === accountId)?.name || form.account_name}
                  />
                </div>
                <p className="text-base-content/40 text-xs font-medium">Parent {ACCOUNT_LABEL.singularLower} cannot be changed after {SUB_ACCOUNT_LABEL.singularLower} creation.</p>
              </div>
            </div>
          </div>
        )}

        {/* Sub-Account Information */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
            <Layers size={18} className="text-primary" />
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              {SUB_ACCOUNT_LABEL.singular} Information
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Sub-Account Name */}
            <div className="space-y-1.5">
              <label htmlFor="name" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                {SUB_ACCOUNT_LABEL.singular} Name *
              </label>
              <input
                id="name"
                type="text"
                maxLength={FIELD_LIMITS.DEFAULT_TEXT}
                placeholder="e.g. Downtown Service Center"
                className={inputClass('name')}
                value={form.name}
                onChange={e => {
                  setForm(f => ({ ...f, name: e.target.value }));
                  if (fieldErrors.name) {
                    setFieldErrors(prev => ({ ...prev, name: validateTextField(e.target.value, `${SUB_ACCOUNT_LABEL.singular} name`, { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT }) }));
                  }
                }}
                onBlur={() => handleFieldBlur('name', form.name, `${SUB_ACCOUNT_LABEL.singular} name`, { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT })}
              />
              {fieldErrors.name && (
                <p className="text-error text-xs font-medium">{fieldErrors.name}</p>
              )}
            </div>

            {/* External Sub-Account ID */}
            <div className="space-y-1.5">
              <label htmlFor="externalSubAccountId" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                External {SUB_ACCOUNT_LABEL.singular} ID
              </label>
              <input
                id="externalSubAccountId"
                type="text"
                maxLength={255}
                placeholder="Optional"
                className={inputClass('externalSubAccountId')}
                value={form.externalSubAccountId}
                onChange={e => setForm(f => ({ ...f, externalSubAccountId: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Service Request Types */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4 relative z-20">
          <div className="flex items-center justify-between pb-3 border-b border-base-content/10">
            <div className="flex items-center gap-2">
              <Layers size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                {SERVICE_REQUEST_LABEL.singular} Types
              </h2>
            </div>
            <span className="text-[10px] text-base-content/40 font-mono">Optional</span>
          </div>

          <div className="space-y-1.5">
            <MultiSelect
              label={`Assigned ${SERVICE_REQUEST_LABEL.singular} Types`}
              searchPlaceholder={`Search ${SERVICE_REQUEST_LABEL.singularLower} types...`}
              placeholder={`Select ${SERVICE_REQUEST_LABEL.singularLower} types (optional)`}
              options={availableServiceRequestTypes.map(t => ({ id: t.uid, key: t.name }))}
              selected={form.serviceRequestTypeIds}
              onChange={(ids) => setForm(f => ({ ...f, serviceRequestTypeIds: ids }))}
            />
          </div>
        </div>

        {/* Sub-Account Branding Section — empty fields fall back to the parent account */}
        <BrandingSection
          title={`${SUB_ACCOUNT_LABEL.singular} Branding`}
          value={form}
          onChange={(patch) => setForm(f => ({ ...f, ...patch }))}
          uploadLogo={handleLogoUpload}
          uploadFavicon={handleFaviconUpload}
          radioName="sub-account-theme"
          inherited={{ source: accountBranding ? `${ACCOUNT_LABEL.singularLower} branding` : 'platform default', ...accountBranding }}
        />

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-2 relative z-0">
          <button
            type="button"
            onClick={() => navigate(ROUTE_SUB_ACCOUNTS)}
            className="w-full sm:w-auto px-6 py-3 border border-base-content/20 text-base-content/70 hover:bg-base-200 rounded-xl font-bold text-sm transition-all"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:flex-1 btn-premium justify-center"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                <span>Saving Changes...</span>
              </>
            ) : (
              <>
                <Save size={16} />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditSubAccount;
